import React, { useContext, useState } from 'react'
import ReactGA from 'react-ga'
import { useHistory, useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { RootContext } from '../../../context/rootContext'

import useSWR from 'swr'

import Alert from '../../../components/Form/Alert'
import { findByPartnerCnpj } from '../../../services/professionals'
import { login } from '../../../services/auth'
import {
  Container,
  InputGroup,
  Label,
  Select,
  InputMask,
  Button,
  ErrorMessage,
  LinkContainer
} from '../../../styles/global'
import { CNPJ_INPUT_STYLE, CNPJ_REGEX, PAGES, PROFESSIONAL_ROLES } from '../../../utils/constants'
import { isMobile as checkIfMobile } from '../../../utils/functionalitiesHelper'
import { formatProfessionalName } from '../../../utils/formatters'
import { Title, Form } from './styles'

const Login = () => {
  const { handleUpdateUserData } = useContext(RootContext)
  const history = useHistory()
  const deviceId = new URLSearchParams(useLocation().search).get('deviceId')
  const source = new URLSearchParams(useLocation().search).get('source')
  const [partner, setPartner] = useState('')
  const [cnpj, setCnpj] = useState('')
  const [professionals, setProfessionals] = useState('')
  const [professional, setProfessional] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [appLoginErrorMessage, setAppLoginErrorMessage] = useState('')
  const [password, setPassword] = useState('')
  const { data: partners, error } = useSWR(
    `${process.env.REACT_APP_PARTNER_URL}/partners/1000`
  )

  if (error) {
    Sentry.captureException(error)
    return error.toString()
  }
  if (!partners) {
    return '...loading'
  }

  const onCnpjChange = async e => {
    const cnpj = e.target.value
    setCnpj(cnpj)
    setProfessional('')

    if (cnpj && CNPJ_REGEX.test(cnpj)) {
      const professionalList = await findByPartnerCnpj({ partner, cnpj })
      if (professionalList) {
        setProfessionals(professionalList)
      }

      if (professionalList && professionalList.length === 1) {
        setProfessional({
          id: professionalList[0].id,
          name: formatProfessionalName(professionalList[0].name),
          cnpj,
          calendarId: professionalList[0].calendarId
        })
      }
    }
  }

  const handleLogin = async () => {
    const isMobile = checkIfMobile()
    const selectedPartner = partners.find(({ id }) => id === partner)
    return await login({
      email: professional.calendarId,
      password,
      partner,
      partnerName: selectedPartner.fantasyName,
      partnerPhone: selectedPartner.phone,
      deviceId,
      source
    }).then(response => {
      if (response.status === 200) {
        const isVet = response.data.role === PROFESSIONAL_ROLES.VETERINARIAN
        handleUpdateUserData({ partner, professional: response.data, partnerName: selectedPartner.fantasyName, partnerPhone: selectedPartner.phone })

        if (isVet) {
          const redirect = `/vets${source === 'app' ? '?source=app' : (isMobile ? '?source=mobile' : '')}`
          history.push(redirect)
        } else {
          history.push('/daily')
          ReactGA.pageview('/daily')
        }
      } else if (source === 'app' && response.status === 403) {
        setAppLoginErrorMessage(response.message)
      } else {
        setErrorMessage(response.message)
      }
    })
  }

  const handleChangePartner = e => {
    setProfessional('')
    setProfessionals('')
    setCnpj('')
    const selectedPartnerId = e.target.value
    setPartner(selectedPartnerId)
  }

  const handleSetProfessional = e => {
    setProfessional({
      id: professionals[e.target.value].id,
      name: formatProfessionalName(professionals[e.target.value].name),
      calendarId: professionals[e.target.value].calendarId,
      cnpj
    })
  }

  const onPasswordChange = async e => {
    const password = e.target.value
    setPassword(password)
    setErrorMessage('')
  }

  const forgotPasswordUrl = () => {
    let url = `${PAGES.FORGOT_PASSWORD_URL}/${professional.id}?email=${professional.calendarId}`
    if (deviceId) url += `&deviceId=${deviceId}`
    if (source) url += `&source=${source}`
    return url
  }

  return (
    <Container>
      <Title>Acessar Calendário</Title>
      <Form>
        <InputGroup>
          <Label>Escolha a Unidade:</Label>
          <Select defaultValue="" onChange={e => handleChangePartner(e)}>
            <option value="">Selecione uma das unidades</option>
            {partners.map(partner => (
              <option key={partner.id} value={partner.id}>
                {partner.fantasyName}
              </option>
            ))}
          </Select>
        </InputGroup>
        <InputGroup>
          <Label>Insira seu CNPJ:</Label>
          <InputMask
            value={cnpj}
            onChange={onCnpjChange}
            placeholder='AB.BCD.EFG/0001-28'
            mask={'**.***.***/****-99'}
            style={CNPJ_INPUT_STYLE}
            disabled={!partner}
          />
          {professionals && professionals.length === 0 && (
            <ErrorMessage>CNPJ não localizado na unidade</ErrorMessage>
          )}
        </InputGroup>
        {professionals.length > 1 && (
          <InputGroup>
            <Label>Selecione o nome</Label>
            <Select
              placeholder="Selecione o profissional"
              onChange={handleSetProfessional}
              defaultValue=""
            >
              <option value="">Selecione um dos profissionais</option>
              {professionals.map((professional, index) => (
                <option key={index} value={index}>
                  {professional.name}
                </option>
              ))}
            </Select>
          </InputGroup>
        )}
        { professional && professionals.length > 0 && (
          <>
            <InputGroup>
              <Label>E-mail</Label>
              <InputMask value={professional.calendarId} disabled={true} />
            </InputGroup>
            <InputGroup className='m-bottom-10'>
              <Label>Senha</Label>
              <InputMask type='password' onChange={onPasswordChange} value={password} />
              <ErrorMessage>{errorMessage}</ErrorMessage>
            </InputGroup>
            <LinkContainer>
              <a href={forgotPasswordUrl()}>Esqueci a senha</a>
            </LinkContainer>
          </>
        )}
        <div style={{ marginBottom: 24 }}>
          <Alert type={'danger'} show={appLoginErrorMessage && source === 'app'}>
            No momento, este aplicativo é para atender demandas específicas de franqueados. Por enquanto, continue a realizar seu acesso no link habitual.
          </Alert>
        </div>
        <Button onClick={handleLogin} disabled={!partner || !professional}>
          Acessar conta
        </Button>
      </Form>
    </Container>
  )
}

export default Login
